import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  ShieldCheck,
  ShieldAlert,
  Play,
  Send,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Settings2,
  RefreshCcw,
  CheckCircle,
  HelpCircle,
  ExternalLink,
  Code2,
  Plus,
  Search,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { cn } from '../utils/cn';
import { eventTeamApiService } from '../services/eventTeamApi';
import { usernameCacheService } from '../services/usernameCache';
import {
  loadClearanceConfig,
  saveClearanceConfig,
  remapQuery,
  expandCommandSlashVariants,
  runTeamQuery,
  DEFAULT_CLEARANCE_CONFIG,
  type ClearanceRunnerConfig,
  type TeamRunResult,
} from '../services/eventClearanceRunner';
import type {
  EsQuerySpec,
  EventCheckResult,
  EventCheckSummary,
  EventCheckViolation,
  EventCommandCheck,
  EventPlayerTeam,
} from '../types/eventTeam';
import { getEventTeamPerms } from '../utils/eventTeamPermissions';
import { checkTeamList } from '../utils/checkTeamList';
import { CommandCheckModal, parseCheckCommands } from '../components/eventteam/CommandCheckModal';
import { CheckRewardsPanel } from '../components/eventteam/CheckRewardsPanel';
import { CheckTeamModal } from '../components/eventteam/CheckTeamModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatorProfile } from '../components/CreatorProfile';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TeamRunState =
  | { phase: 'pending' }
  | { phase: 'running' }
  | { phase: 'done'; result: TeamRunResult }
  | { phase: 'error'; message: string };

const VERDICT_CHIP: Record<string, string> = {
  clear: 'bg-green-500/15 text-green-300 border-green-500/25',
  flagged: 'bg-red-500/15 text-red-300 border-red-500/25',
  unchecked: 'bg-glass-200/10 text-glass-400 border-glass-200/20',
};

const CHECK_STATUS_CHIP: Record<string, string> = {
  draft: 'bg-glass-200/10 text-glass-400 border-glass-200/20',
  ready: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
  completed: 'bg-green-500/15 text-green-300 border-green-500/25',
};

function ViolationsTable({ violations }: { violations: EventCheckViolation[] | { user_uuid: string; command: string; used_at?: number | null; server?: string | null; es_doc_ref?: string | null }[] }) {
  if (violations.length === 0) return <p className="text-xs text-glass-500">No violations.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-glass-200/10">
      <table className="table text-xs">
        <thead>
          <tr>
            <th className="th py-2">Player</th>
            <th className="th py-2">Command</th>
            <th className="th py-2">Used at</th>
            <th className="th py-2">Server</th>
            <th className="th py-2">Doc ref</th>
          </tr>
        </thead>
        <tbody>
          {violations.map((v, i) => (
            <tr key={('id' in v && v.id) || `${v.user_uuid}-${i}`}>
              <td className="td py-2">
                <PlayerProfile uuid={v.user_uuid} showUuid={false} inline />
              </td>
              <td className="td py-2 font-mono text-red-300">{v.command}</td>
              <td className="td py-2 text-glass-400 tabular-nums">
                {v.used_at ? format(new Date(v.used_at), 'MMM d, HH:mm:ss') : '—'}
              </td>
              <td className="td py-2 text-glass-400 font-mono">{v.server || '—'}</td>
              <td className="td py-2 text-glass-500 font-mono text-[10px]">{v.es_doc_ref || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Clearance check runner (guide §3.3): the browser executes the backend-generated
 * ES queries with the operator's Cloudflare Access session, then submits per-team
 * clear/flagged results (+violations) as a new run.
 */
export const EventCheckDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getEventTeamPerms(identity, isDev), [identity, isDev]);

  const [check, setCheck] = useState<EventCommandCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<EventCheckSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [expandedSummaryTeam, setExpandedSummaryTeam] = useState<string | null>(null);

  // Runs history
  const [results, setResults] = useState<EventCheckResult[]>([]);
  const [expandedResultId, setExpandedResultId] = useState<string | null>(null);
  const [resultViolations, setResultViolations] = useState<EventCheckViolation[] | null>(null);
  const [violationsLoading, setViolationsLoading] = useState(false);

  // Runner state
  /**
   * All player teams, loaded up-front — the operator picks teams DIRECTLY and we
   * pass explicit `team_ids` to `/es-query`. No need to bind teams to an event
   * and the event to the check first (Roll/sauramel feedback: too many steps).
   * The list starts at the check's own event teams, though: every team from
   * every past event is a long scroll (Roll feedback).
   */
  const [allTeams, setAllTeams] = useState<EventPlayerTeam[] | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [teamQuery, setTeamQuery] = useState('');
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [spec, setSpec] = useState<EsQuerySpec | null>(null);
  const [specLoading, setSpecLoading] = useState(false);
  const [specError, setSpecError] = useState<string | null>(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [runStates, setRunStates] = useState<Record<string, TeamRunState>>({});
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [expandedRunTeam, setExpandedRunTeam] = useState<string | null>(null);

  // Deployment config
  const [config, setConfig] = useState<ClearanceRunnerConfig>(() => loadClearanceConfig());
  const [showConfig, setShowConfig] = useState(false);
  const [queryPreviewTeam, setQueryPreviewTeam] = useState<string | null>(null);
  const [queryOverrides, setQueryOverrides] = useState<Record<string, string>>({});

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useDocumentTitle(check ? `Check: ${check.title}` : 'Command Check');

  const loadCheck = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const data = await eventTeamApiService.getCheck(id);
      setCheck(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load check');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadSummaryAndResults = useCallback(async () => {
    if (!id) return;
    setSummaryError(null);
    try {
      const [summaryRes, resultsRes] = await Promise.all([
        eventTeamApiService.getCheckSummary(id).catch((err) => {
          setSummaryError(err instanceof Error ? err.message : 'Failed to load summary');
          return null;
        }),
        eventTeamApiService.getCheckResults(id).catch(() => [] as EventCheckResult[]),
      ]);
      if (summaryRes) setSummary(summaryRes);
      setResults(resultsRes);
    } catch {
      /* handled above */
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void loadCheck();
    void loadSummaryAndResults();
  }, [loadCheck, loadSummaryAndResults]);

  // ── Runner ──────────────────────────────────────────────────────────────

  const loadAllTeams = useCallback(async () => {
    setTeamsError(null);
    try {
      const res = await eventTeamApiService.listCheckTeams({ limit: 200 });
      setAllTeams(res.results);
    } catch (err) {
      setAllTeams([]);
      setTeamsError(err instanceof Error ? err.message : 'Failed to load player teams');
    }
  }, []);

  useEffect(() => {
    if (perms.canLead) void loadAllTeams();
  }, [perms.canLead, loadAllTeams]);

  const teamList = useMemo(
    () =>
      allTeams &&
      checkTeamList(allTeams, { eventId: check?.event_id ?? null, showAll: showAllTeams, selected: selectedTeamIds, query: teamQuery }),
    [allTeams, check?.event_id, showAllTeams, selectedTeamIds, teamQuery],
  );

  // Preselect sensibly: teams already linked to this check's event, else a lone team.
  useEffect(() => {
    if (!allTeams || !check) return;
    setSelectedTeamIds((prev) => {
      if (prev.size > 0) return prev;
      const eventLinked = check.event_id ? allTeams.filter((t) => t.event_id === check.event_id) : [];
      if (eventLinked.length > 0) return new Set(eventLinked.map((t) => t.id));
      if (allTeams.length === 1) return new Set([allTeams[0].id]);
      return prev;
    });
  }, [allTeams, check]);


  /** Fetch query specs for EXACTLY the selected teams (explicit `team_ids`). */
  const loadSpec = async (): Promise<EsQuerySpec | null> => {
    if (!id || selectedTeamIds.size === 0) return null;
    setSpecLoading(true);
    setSpecError(null);
    setSubmitted(false);
    setSubmitError(null);
    try {
      const specRes = await eventTeamApiService.getCheckEsQuery(id, Array.from(selectedTeamIds));
      setSpec(specRes);
      usernameCacheService.prefetchUsernames(specRes.teams.flatMap((t) => t.member_uuids));
      if (specRes.teams.length === 0) {
        setSpecError('No queries returned for the selected teams.');
        return null;
      }
      return specRes;
    } catch (err) {
      setSpecError(err instanceof Error ? err.message : 'Failed to fetch the ES query spec');
      return null;
    } finally {
      setSpecLoading(false);
    }
  };

  const effectiveQueryJson = (teamId: string): string => {
    if (queryOverrides[teamId] != null) return queryOverrides[teamId];
    const team = spec?.teams.find((t) => t.team_id === teamId);
    if (!team || !spec) return '{}';
    let query = remapQuery(team.query, spec.field_hints, config.mapping, config.stripKeywordSuffixes);
    if (config.normalizeCommandSlashes) {
      query = expandCommandSlashVariants(query, config.mapping.command);
    }
    return JSON.stringify(query, null, 2);
  };

  /** ONE click: fetch specs for the ticked teams (if stale) and run them all. */
  const handleRun = async () => {
    setRunning(true);
    setRunStates({});
    const activeSpec = await loadSpec();
    if (!activeSpec) { setRunning(false); return; }
    const teams = activeSpec.teams.filter((t) => selectedTeamIds.has(t.team_id));
    if (teams.length === 0) { setRunning(false); return; }
    setSubmitted(false);
    setSubmitError(null);
    saveClearanceConfig(config);

    const nextStates: Record<string, TeamRunState> = {};
    const completed: TeamRunResult[] = [];
    for (const t of teams) nextStates[t.team_id] = { phase: 'pending' };
    setRunStates({ ...nextStates });

    // Sequential execution keeps ES load light and progress readable.
    for (const team of teams) {
      setRunStates((prev) => ({ ...prev, [team.team_id]: { phase: 'running' } }));
      try {
        let override: Record<string, unknown> | undefined;
        if (queryOverrides[team.team_id] != null) {
          try {
            override = JSON.parse(queryOverrides[team.team_id]) as Record<string, unknown>;
          } catch {
            throw new Error('The edited query JSON is not valid JSON.');
          }
        }
        const result = await runTeamQuery(team, activeSpec.field_hints, config, override);
        completed.push(result);
        setRunStates((prev) => ({ ...prev, [team.team_id]: { phase: 'done', result: { ...result, check_signature: activeSpec.check_signature } } }));
      } catch (err) {
        setRunStates((prev) => ({
          ...prev,
          [team.team_id]: { phase: 'error', message: err instanceof Error ? err.message : String(err) },
        }));
      }
    }
    if (id && completed.length === teams.length) {
      setSubmitting(true);
      try {
        await eventTeamApiService.submitCheckResults(id, completed.map(r => ({ team_id: r.team_id, status: r.status, members_checked: r.members_checked, checked_uuids: r.checked_uuids, violations: r.violations })), activeSpec.check_signature);
        setSubmitted(true);
        await loadCheck();
        await loadSummaryAndResults();
      } catch (err) { setSubmitError(err instanceof Error ? err.message : 'The check ran, but its results could not be saved. Retry saving below.'); }
      finally { setSubmitting(false); }
    }
    setRunning(false);
  };

  const runOutcomes = useMemo(() => {
    const selected = Array.from(selectedTeamIds);
    const done = selected.filter((id2) => runStates[id2]?.phase === 'done');
    const failed = selected.filter((id2) => runStates[id2]?.phase === 'error');
    const allDone = selected.length > 0 && done.length === selected.length;
    return { done, failed, allDone };
  }, [selectedTeamIds, runStates]);

  /** No partial results — every selected team must have succeeded (guide §3.3). */
  const handleSubmitResults = async () => {
    if (!id || !runOutcomes.allDone) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = Array.from(selectedTeamIds).map((teamId) => {
        const state = runStates[teamId];
        if (state?.phase !== 'done') throw new Error('Not all teams have results.');
        const r = state.result;
        return {
          team_id: r.team_id,
          status: r.status,
          members_checked: r.members_checked,
          checked_uuids: r.checked_uuids,
          violations: r.violations.length > 0 ? r.violations : undefined,
        };
      });
      const signatures = Array.from(selectedTeamIds).map(teamId => { const state = runStates[teamId]; return state?.phase === 'done' ? state.result.check_signature : undefined; });
      if (new Set(signatures).size !== 1) throw new Error('The check changed between group searches. Run all groups again.');
      await eventTeamApiService.submitCheckResults(id, payload, signatures[0]);
      setSubmitted(true);
      await loadCheck();
      await loadSummaryAndResults();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit results');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleResultViolations = async (result: EventCheckResult) => {
    if (expandedResultId === result.id) {
      setExpandedResultId(null);
      setResultViolations(null);
      return;
    }
    setExpandedResultId(result.id);
    setResultViolations(null);
    if (result.status === 'clear') {
      setResultViolations([]);
      return;
    }
    setViolationsLoading(true);
    try {
      const v = await eventTeamApiService.getResultViolations(result.id);
      setResultViolations(v);
      usernameCacheService.prefetchUsernames(v.map((x) => x.user_uuid));
    } catch {
      setResultViolations([]);
    } finally {
      setViolationsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await eventTeamApiService.deleteCheck(id);
      navigate('/event-team?tab=clearance');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete check');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  // Runs grouped newest-first (re-running never overwrites — guide §3.3).
  const runGroups = useMemo(() => {
    const groups = new Map<string, EventCheckResult[]>();
    for (const r of results) {
      const list = groups.get(r.run_id) ?? [];
      list.push(r);
      groups.set(r.run_id, list);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      const aAt = Math.max(...a[1].map((r) => r.checked_at ?? 0));
      const bAt = Math.max(...b[1].map((r) => r.checked_at ?? 0));
      return bAt - aAt;
    });
  }, [results]);

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-2">
          <SkeletonLoader className="h-3 w-24" />
          <SkeletonLoader className="h-7 w-72 max-w-full" />
          <div className="flex gap-2 pt-1">
            <SkeletonLoader className="h-5 w-24 rounded-md" />
            <SkeletonLoader className="h-5 w-24 rounded-md" />
          </div>
          <SkeletonLoader className="h-3 w-96 max-w-full" />
        </div>
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="glass-card p-4 md:p-5 space-y-3">
            <SkeletonLoader className="h-4 w-40" />
            <SkeletonLoader className="h-11 w-full" />
            <SkeletonLoader className="h-11 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !check || !id) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Event Team"
          title="Command check"
          onBack={() => navigate('/event-team?tab=clearance')}
          backLabel="Event Team"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load check"
          description={error || 'Check not found'}
          action={
            <>
              <button type="button" onClick={() => void loadCheck()} className="glass-button">
                Retry
              </button>
              <button
                type="button"
                onClick={() => navigate('/event-team?tab=clearance')}
                className="glass-button-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Clearance
              </button>
            </>
          }
        />
      </div>
    );
  }

  const commands = parseCheckCommands(check.commands);

  return (
    <div className="space-y-6">
      {/* Definition */}
      <PageHeader
        eyebrow="Event Team"
        backTo="/event-team?tab=clearance"
        backLabel="Event Team"
        icon={ShieldCheck}
        title={check.title}
        titleAdornment={
          <span
            className={cn('badge badge-dot capitalize', CHECK_STATUS_CHIP[check.status] ?? CHECK_STATUS_CHIP.draft)}
          >
            {check.status}
          </span>
        }
        description={check.notes ? <span className="italic">{check.notes}</span> : undefined}
        actions={
          perms.canLead ? (
            <>
              <button type="button" onClick={() => setIsEditOpen(true)} className="glass-button">
                <Pencil className="w-4 h-4" />
                Edit
              </button>
              <button type="button" onClick={() => setIsDeleteOpen(true)} className="btn-danger">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </>
          ) : undefined
        }
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="eyebrow mr-1">Commands</span>
          {commands.map((cmd) => (
            <span key={cmd} className="chip font-mono bg-red-500/10 text-red-300 border-red-500/20">
              {cmd}
            </span>
          ))}
          <span className="text-xs text-glass-400 capitalize">({check.match_mode} match)</span>
        </div>
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-glass-400 tabular-nums">
          <span>
            Window:{' '}
            <span className="text-glass-200">
              {format(new Date(check.time_start), 'MMM d yyyy, HH:mm')} →{' '}
              {format(new Date(check.time_end), 'MMM d yyyy, HH:mm')}
            </span>
          </span>
          {check.scope_fqdn && (
            <span>
              Server: <span className="text-blue-300 font-mono">{check.scope_fqdn}</span>
            </span>
          )}
          {check.scope_cluster && (
            <span>
              Cluster: <span className="text-violet-300 font-mono">{check.scope_cluster}</span>
            </span>
          )}
          {check.event_id && (
            <button
              type="button"
              onClick={() => navigate(`/event-team/events/${check.event_id}`)}
              className="text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Linked event
            </button>
          )}
          <span className="text-glass-500 inline-flex items-center gap-1">
            Created by <CreatorProfile creatorId={check.created_by} inline />{' '}
            {formatDistanceToNow(new Date(check.created_at), { addSuffix: true })}
          </span>
        </div>
      </PageHeader>

      {/* ── Summary: "what teams are clear" ─────────────────────────────── */}
      <CheckRewardsPanel checkId={id!} eventId={check.event_id} runId={summary?.run_id ?? null} windowEnd={check.time_end} perms={perms} />
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          as="h2"
          title="Latest team verdicts"
          icon={CheckCircle}
          iconClassName="text-green-400"
          actions={
            <button
              type="button"
              onClick={() => void loadSummaryAndResults()}
              className="btn-icon"
              title="Refresh"
              aria-label="Refresh latest verdict"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
          }
        />
        <p className="text-sm text-glass-400 mb-3">Teams pass or fail together. If any member uses a prohibited command, the whole team fails and receives no check reward. Expand a team to see who used the command.</p>
        {summaryError && (
          <div className="alert alert-danger mb-3" role="alert">
            <AlertCircle />
            <span className="flex-1">{summaryError}</span>
          </div>
        )}
        {!summary || summary.teams.length === 0 ? (
          <EmptyState bare compact icon={ShieldCheck} title="No results" description="Run the check to record a verdict." />
        ) : (
          <div className="space-y-2">
            {summary.teams.map((team) => {
              const expanded = expandedSummaryTeam === team.team_id;
              return (
                <div key={team.team_id} className="subcard">
                  <button
                    type="button"
                    onClick={() => setExpandedSummaryTeam(expanded ? null : team.team_id)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left flex-wrap"
                    aria-expanded={expanded}
                  >
                    <span className="flex items-center gap-2 flex-wrap">
                      {expanded ? (
                        <ChevronDown className="w-3.5 h-3.5 text-glass-400" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-glass-400" />
                      )}
                      <span className="text-sm text-white font-medium">{team.team_name}</span>
                      <span
                        className={cn('badge capitalize', VERDICT_CHIP[team.status] ?? VERDICT_CHIP.unchecked)}
                      >
                        {team.status === 'clear' ? (
                          <ShieldCheck className="w-3 h-3" />
                        ) : team.status === 'flagged' ? (
                          <ShieldAlert className="w-3 h-3" />
                        ) : (
                          <HelpCircle className="w-3 h-3" />
                        )}
                        {team.status === 'clear' ? 'Team passed' : team.status === 'flagged' ? 'Team failed' : team.status}
                      </span>
                    </span>
                    <span className="text-[11px] text-glass-500 tabular-nums">
                      {team.members_checked != null && `${team.members_checked} members checked · `}
                      {team.violations_count ?? team.violations?.length ?? 0} violation
                      {(team.violations_count ?? team.violations?.length ?? 0) === 1 ? '' : 's'}
                    </span>
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 border-t border-glass-200/10 pt-2">
                      {team.status === 'unchecked' ? (
                        <p className="text-xs text-glass-500">This team was missing from the latest run.</p>
                      ) : (
                        <ViolationsTable violations={team.violations ?? []} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Runner (lead) ───────────────────────────────────────────────── */}
      {perms.canLead && (
        <div className="glass-card p-4 md:p-5 space-y-4">
          <SectionHeader
            as="h2"
            title="Run check"
            icon={Play}
            className="mb-0"
            actions={
              <>
                <button
                  type="button"
                  onClick={() => setShowConfig(!showConfig)}
                  className={cn('glass-button-secondary btn-xs', showConfig && '!text-accent-300')}
                  aria-expanded={showConfig}
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Deployment config
                </button>
                <button
                  type="button"
                  onClick={() => void loadSpec()}
                  disabled={specLoading || running || selectedTeamIds.size === 0}
                  className="glass-button-secondary btn-xs"
                  title="Fetch the generated queries without running them (for the per-team preview)"
                >
                  {specLoading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Code2 className="w-3.5 h-3.5" />}
                  Preview queries
                </button>
              </>
            }
          />

          {/* Deployment config editor */}
          {showConfig && (
            <div className="subcard p-3 space-y-3">
              <p className="text-xs text-glass-400">Index field names. Saved in this browser.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <label className="block text-xs font-medium text-glass-400">
                  Index
                  <input
                    type="text"
                    value={config.index}
                    onChange={(e) => setConfig({ ...config, index: e.target.value })}
                    className="glass-input w-full text-xs font-mono mt-1.5"
                  />
                </label>
                {(
                  [
                    ['player_uuid', 'Player UUID field'],
                    ['command', 'Command field'],
                    ['timestamp', 'Timestamp field'],
                    ['server', 'Server field'],
                    ['cluster', 'Cluster field'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="block text-xs font-medium text-glass-400">
                    {label}
                    <input
                      type="text"
                      value={config.mapping[key]}
                      onChange={(e) => setConfig({ ...config, mapping: { ...config.mapping, [key]: e.target.value } })}
                      className="glass-input w-full text-xs font-mono mt-1.5"
                    />
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-glass-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.stripKeywordSuffixes}
                  onChange={(e) => setConfig({ ...config, stripKeywordSuffixes: e.target.checked })}
                  className="form-checkbox"
                />
                Strip <span className="font-mono">.keyword</span> suffixes (required for{' '}
                <span className="font-mono">cmd_logs_v1</span>)
              </label>
              <label className="flex items-center gap-2 text-xs text-glass-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.normalizeCommandSlashes}
                  onChange={(e) => setConfig({ ...config, normalizeCommandSlashes: e.target.checked })}
                  className="form-checkbox"
                />
                Match commands with and without the leading slash
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    saveClearanceConfig(config);
                    alert('Deployment config saved for this browser.');
                  }}
                  className="glass-button-secondary btn-xs"
                >
                  Save config
                </button>
                <button
                  onClick={() =>
                    setConfig({ ...DEFAULT_CLEARANCE_CONFIG, mapping: { ...DEFAULT_CLEARANCE_CONFIG.mapping } })
                  }
                  className="glass-button-secondary btn-xs"
                >
                  Reset to defaults
                </button>
              </div>
            </div>
          )}

          {specError && (
            <div className="alert alert-warning" role="alert">
              <AlertCircle />
              <span className="flex-1">{specError}</span>
            </div>
          )}

          {teamsError && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span className="flex-1">{teamsError}</span>
            </div>
          )}

          {/* Team checklist — this event's teams first; ANY team is a click away (no event binding required). */}
          {allTeams == null || teamList == null ? (
            <div className="space-y-2" aria-busy="true" aria-label="Loading player teams">
              <SkeletonLoader className="h-11 w-full" />
              <SkeletonLoader className="h-11 w-full" />
            </div>
          ) : allTeams.length === 0 ? (
            <EmptyState
              bare
              compact
              icon={ShieldCheck}
              title="No player teams"
              description="Create a team to choose who this check runs against."
              action={
                <button type="button" onClick={() => setShowCreateTeam(true)} className="glass-button">
                  <Plus className="w-4 h-4" />
                  New team
                </button>
              }
            />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {check.event_id && (
                  <div className="segmented" role="group" aria-label="Teams to list">
                    <button
                      type="button"
                      onClick={() => setShowAllTeams(false)}
                      className="segmented-item segmented-item-sm"
                      data-active={teamList.listingEventTeams ? 'true' : 'false'}
                      aria-pressed={teamList.listingEventTeams}
                    >
                      This event's teams ({teamList.eventTeamCount})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAllTeams(true)}
                      className="segmented-item segmented-item-sm"
                      data-active={teamList.listingEventTeams ? 'false' : 'true'}
                      aria-pressed={!teamList.listingEventTeams}
                    >
                      All teams ({allTeams.length})
                    </button>
                  </div>
                )}
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                  <input
                    type="search"
                    value={teamQuery}
                    onChange={(e) => setTeamQuery(e.target.value)}
                    placeholder="Find a team…"
                    aria-label="Find a team"
                    className="glass-input w-full pl-9"
                  />
                </div>
              </div>
              {teamList.visible.length === 0 &&
                (teamQuery.trim() ? (
                  <p className="text-sm text-glass-400">
                    No teams match “{teamQuery.trim()}”{teamList.listingEventTeams ? ' in this event. Try All teams.' : '.'}
                  </p>
                ) : (
                  <div className="subcard p-3 flex flex-wrap items-center gap-2">
                    <p className="text-sm text-glass-400 flex-1 min-w-[200px]">
                      No teams are linked to this event yet. Create one here, or set “Linked event” on a team under Groups.
                    </p>
                    <button type="button" onClick={() => setShowCreateTeam(true)} className="glass-button-secondary btn-xs">
                      <Plus className="w-3.5 h-3.5" />
                      New team for this event
                    </button>
                    <button type="button" onClick={() => setShowAllTeams(true)} className="glass-button-secondary btn-xs">
                      Show all teams
                    </button>
                  </div>
                ))}
              <div className="space-y-2">
                {teamList.visible.map((team) => {
                  const selected = selectedTeamIds.has(team.id);
                  const state = runStates[team.id];
                  const expanded = expandedRunTeam === team.id;
                  const previewOpen = queryPreviewTeam === team.id;
                  const specTeam = spec?.teams.find((t) => t.team_id === team.id);
                  const eventLinked = check.event_id != null && team.event_id === check.event_id;
                  return (
                    <div key={team.id} className="subcard">
                      <div className="flex items-center justify-between gap-2 px-3 py-2 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer min-w-0">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={running}
                            onChange={(e) =>
                              setSelectedTeamIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(team.id);
                                else next.delete(team.id);
                                return next;
                              })
                            }
                            className="form-checkbox"
                          />
                          {team.color && (
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
                          )}
                          <span className="text-sm text-white font-medium truncate">{team.name}</span>
                          {specTeam && (
                            <span className="text-[11px] text-glass-500">{specTeam.member_uuids.length} players</span>
                          )}
                          {eventLinked && (
                            <span className="badge badge-accent">this event</span>
                          )}
                        </label>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {specTeam && (
                            <button
                              type="button"
                              onClick={() => setQueryPreviewTeam(previewOpen ? null : team.id)}
                              className="btn-icon btn-icon-sm"
                              title="View / edit the final (remapped) query JSON"
                              aria-label="View or edit the final query JSON"
                              aria-expanded={previewOpen}
                            >
                              <Code2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {state?.phase === 'running' && <Loader className="w-4 h-4 animate-spin text-accent-400" />}
                          {state?.phase === 'done' && (
                            <button
                              onClick={() => setExpandedRunTeam(expanded ? null : team.id)}
                              className={cn('badge capitalize cursor-pointer', VERDICT_CHIP[state.result.status])}
                              aria-expanded={expanded}
                            >
                              {state.result.status === 'clear' ? (
                                <ShieldCheck className="w-3 h-3" />
                              ) : (
                                <ShieldAlert className="w-3 h-3" />
                              )}
                              {state.result.status === 'clear' ? 'Team passed' : 'Team failed'} · {state.result.hits} hit{state.result.hits === 1 ? '' : 's'}
                              {state.result.capped && ' (capped)'}
                            </button>
                          )}
                          {state?.phase === 'error' && (
                            <span className="badge badge-danger">failed</span>
                          )}
                        </div>
                      </div>

                      {previewOpen && specTeam && (
                        <div className="px-3 pb-3 border-t border-glass-200/10 pt-2 space-y-1">
                          <p className="text-[10px] text-glass-500">Final query. Edits are used verbatim.</p>
                          <textarea
                            value={effectiveQueryJson(team.id)}
                            onChange={(e) =>
                              setQueryOverrides((prev) => ({ ...prev, [team.id]: e.target.value }))
                            }
                            className="glass-input w-full text-[11px] font-mono min-h-[160px]"
                            spellCheck={false}
                          />
                          {queryOverrides[team.id] != null && (
                            <button
                              onClick={() =>
                                setQueryOverrides((prev) => {
                                  const next = { ...prev };
                                  delete next[team.id];
                                  return next;
                                })
                              }
                              className="text-[11px] text-accent-300 hover:text-accent-200 hover:underline underline-offset-2"
                            >
                              Reset to the generated query
                            </button>
                          )}
                        </div>
                      )}

                      {state?.phase === 'error' && (
                        <div className="px-3 pb-2 border-t border-glass-200/10 pt-2">
                          <p className="text-xs text-red-300">{state.message}</p>
                        </div>
                      )}

                      {state?.phase === 'done' && expanded && (
                        <div className="px-3 pb-3 border-t border-glass-200/10 pt-2">
                          {state.result.capped && (
                            <p className="text-[11px] text-amber-300 mb-2">
                              Safety cap reached ({config.maxHitsPerTeam} hits). List truncated.
                            </p>
                          )}
                          <ViolationsTable violations={state.result.violations} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {runOutcomes.failed.length > 0 && (
                <div className="alert alert-danger" role="alert">
                  <AlertCircle />
                  <span className="flex-1">
                    {runOutcomes.failed.length} quer{runOutcomes.failed.length === 1 ? 'y' : 'ies'} failed. Nothing was
                    submitted. Re-run or deselect the failed teams.
                  </span>
                </div>
              )}

              {submitError && (
                <div className="alert alert-danger" role="alert">
                  <AlertCircle />
                  <span className="flex-1">{submitError}</span>
                </div>
              )}

              {submitted && (
                <div className="alert alert-success" role="status">
                  <CheckCircle />
                  <span className="flex-1">Results saved. Review the check summary above.</span>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => void handleRun()}
                  disabled={running || submitting || specLoading || selectedTeamIds.size === 0}
                  className="btn-primary"
                >
                  {running || specLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Run & save check ({selectedTeamIds.size} team{selectedTeamIds.size === 1 ? '' : 's'})
                </button>
                {!submitted && submitError && <button
                  type="button"
                  onClick={() => void handleSubmitResults()}
                  disabled={!runOutcomes.allDone || running || submitting || submitted}
                  className="glass-button"
                  title={
                    runOutcomes.allDone
                      ? 'Submit per-team results as a new run'
                      : 'All selected teams must run successfully before submitting'
                  }
                >
                  {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Retry saving results
                </button>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Runs history ────────────────────────────────────────────────── */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          as="h2"
          title="Runs history"
          icon={RefreshCcw}
          iconClassName="text-glass-400"
          count={runGroups.length}
        />
        {runGroups.length === 0 ? (
          <EmptyState bare compact icon={RefreshCcw} title="No runs recorded" />
        ) : (
          <div className="space-y-3">
            {runGroups.map(([runId, runResults], idx) => (
              <div key={runId} className="subcard p-3">
                <p className="text-[11px] text-glass-500 mb-2 tabular-nums">
                  Run <span className="font-mono">{runId.slice(0, 8)}</span>
                  {idx === 0 && <span className="text-accent-300 ml-1">(latest)</span>} ·{' '}
                  {runResults[0]?.checked_at
                    ? format(new Date(runResults[0].checked_at), 'MMM d yyyy, HH:mm')
                    : ''}
                </p>
                <div className="divide-y divide-glass-200/10">
                  {runResults.map((r) => {
                    const expanded = expandedResultId === r.id;
                    return (
                      <div key={r.id}>
                        <button
                          type="button"
                          onClick={() => void toggleResultViolations(r)}
                          className="w-full flex items-center justify-between gap-2 px-1 py-2 text-left flex-wrap rounded-md hover:bg-glass-200/5 transition-colors"
                          aria-expanded={expanded}
                        >
                          <span className="flex items-center gap-2">
                            {expanded ? (
                              <ChevronDown className="w-3 h-3 text-glass-400" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-glass-400" />
                            )}
                            <span className="text-sm text-glass-200">{r.team_name || r.team_id}</span>
                            <span
                              className={cn('badge capitalize', VERDICT_CHIP[r.status] ?? VERDICT_CHIP.unchecked)}
                            >
                              {r.status}
                            </span>
                          </span>
                          <span className="text-[11px] text-glass-500 inline-flex items-center gap-1">
                            {r.members_checked} members · by <CreatorProfile creatorId={r.checked_by} inline />
                          </span>
                        </button>
                        {expanded && (
                          <div className="px-1 pb-2 pt-1">
                            {violationsLoading ? (
                              <Loader className="w-4 h-4 animate-spin text-glass-400" />
                            ) : (
                              <ViolationsTable violations={resultViolations ?? []} />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateTeam && (
        <CheckTeamModal
          isOpen={showCreateTeam}
          mode="create"
          presetEventId={check.event_id ?? undefined}
          onClose={() => setShowCreateTeam(false)}
          onSuccess={(created) => {
            setShowCreateTeam(false);
            setSelectedTeamIds((prev) => new Set(prev).add(created.id));
            void loadAllTeams();
          }}
        />
      )}
      {isEditOpen && (
        <CommandCheckModal
          isOpen={isEditOpen}
          mode="edit"
          check={check}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            void loadCheck();
          }}
        />
      )}
      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => void handleDelete()}
          title="Delete Command Check"
          message={`Delete "${check.title}"? All recorded runs and violations cascade.`}
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
